import { Component, type ErrorInfo, type ReactNode } from "react";

import { ErrorSurface } from "@/components/error-surfaces/error-surface";
import { MaintenanceScreen } from "@/components/maintenance-takeover";
import { reloadPage } from "@/components/error-surfaces/reload-page";
import { Button } from "@/components/ui/button";
import { useFeatureFlag, useFeatureFlagPayload } from "@/hooks/feature-flags";

/**
 * The last resort. TanStack Router already catches throws inside route
 * components and sends them to the route's `errorComponent` (the 500 surface),
 * so this boundary is not that path — it is everything the router's own
 * boundaries sit inside of or outside of: the root layout, the app-wide
 * providers, and an error component that itself throws. Without it those throws
 * unmount the tree and leave a blank page.
 *
 * A class component because `getDerivedStateFromError` is the only React API
 * that catches a render throw; there is no hook equivalent.
 *
 * Separate from `components/ErrorBoundary.tsx`, which is the (still unmounted)
 * Sentry boundary and rides the Sentry integration work.
 */
export class AppCrashBoundary extends Component<
  { children: ReactNode; onReload?: () => void },
  { crashed: boolean }
> {
  state = { crashed: false };

  static getDerivedStateFromError() {
    return { crashed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // The console is the only sink until Sentry lands; the surface itself never
    // shows the message, because a crash here can be anywhere in the app and
    // the throw may carry internals.
    console.error("App crash boundary caught a render error", error, info.componentStack);
  }

  render() {
    if (!this.state.crashed) return this.props.children;

    // `reloadPage` rather than a boundary reset: the tree that just threw is
    // still the tree we would re-render, so clearing this state alone crashes
    // again. `onReload` is the seam the boundary's own test drives.
    return <CrashFallback onReload={this.props.onReload ?? reloadPage} />;
  }
}

// A function component (the boundary itself can't host hooks) so the fallback
// can keep reading the maintenance flag: the boundary latches until reload,
// and this crashed subtree is *outside* the takeover it just unmounted — so
// without its own branch, a maintenance window starting after a crash would
// stay invisible for as long as the visitor didn't reload (#153: maintenance
// outranks every error surface, this latched one included).
function CrashFallback({ onReload }: { onReload: () => void }) {
  const maintenance = useFeatureFlag("enable_maintenance");
  const maintenancePayload = useFeatureFlagPayload("enable_maintenance");

  if (maintenance) return <MaintenanceScreen backAt={maintenancePayload?.back_at} />;

  return (
    <ErrorSurface
      title="The app hit an unexpected error"
      message="Sorry about that. Reloading the page usually clears it up. If it keeps happening, let the team know."
      actions={
        <Button className="w-full" onClick={onReload}>
          Reload the page
        </Button>
      }
    />
  );
}
